import React, { useState, useMemo } from 'react';
import {
  Modal, Tabs, Card, Row, Col, Statistic, Table, Tag, Progress, Select, Space, Button, Typography, Empty, Divider, Segmented
} from 'antd';
import {
  BarChartOutlined, TrophyOutlined, TeamOutlined, CalendarOutlined,
  RiseOutlined, FallOutlined, SwapOutlined, DownloadOutlined,
  SyncOutlined, ReadOutlined, StarOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const InstitutionAcademicYearComparisonModal = ({
  open,
  onClose,
  data,
  loading,
  onRefresh,
  onMediumChange,
  selectedMedium = 'all'
}) => {
  const institution = data?.institution || {};
  const comparisonData = data?.comparisonData || [];
  const summary = data?.summary || {};
  const availableMediums = summary?.available_mediums || [];

  // Local medium filter state
  const [currentMedium, setCurrentMedium] = useState(selectedMedium || 'all');

  // Dual-year comparison selection
  const [year1Id, setYear1Id] = useState(null);
  const [year2Id, setYear2Id] = useState(null);

  // Set default selected years for dual comparison when comparisonData loads
  React.useEffect(() => {
    if (comparisonData && comparisonData.length > 0) {
      if (comparisonData.length >= 2) {
        setYear1Id(comparisonData[comparisonData.length - 2].academic_year_id);
        setYear2Id(comparisonData[comparisonData.length - 1].academic_year_id);
      } else {
        setYear1Id(comparisonData[0].academic_year_id);
        setYear2Id(comparisonData[0].academic_year_id);
      }
    }
  }, [comparisonData]);

  const year1Data = useMemo(() => {
    return comparisonData.find(y => y.academic_year_id === year1Id) || null;
  }, [comparisonData, year1Id]);

  const year2Data = useMemo(() => {
    return comparisonData.find(y => y.academic_year_id === year2Id) || null;
  }, [comparisonData, year2Id]);

  const handleMediumSelect = (val) => {
    setCurrentMedium(val);
    if (onMediumChange) {
      onMediumChange(val);
    }
  };

  // Export comparison table to CSV
  const handleExportCSV = () => {
    if (!comparisonData || comparisonData.length === 0) return;

    const headers = [
      'Academic Year',
      'Medium Scope',
      'Total Candidates',
      'YoY Candidate Growth (%)',
      'Average Score (%)',
      'Pass Rate (%)',
      'Passed Candidates',
      'Distinction Count',
      'First Class Count',
      'Second Class Count',
      'Pass Class Count',
      'Fail Count',
      'Highest Score (%)'
    ];

    const rows = comparisonData.map(y => [
      `"${y.academic_year}"`,
      `"${currentMedium.toUpperCase()}"`,
      y.total_candidates,
      y.candidate_growth_pct !== null ? `${y.candidate_growth_pct}%` : 'N/A',
      `${y.avg_percentage}%`,
      `${y.pass_rate}%`,
      y.passed_count,
      y.distinction_count,
      y.first_class_count,
      y.second_class_count,
      y.pass_class_count,
      y.fail_count,
      `${y.max_percentage}%`
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${institution.institution_name || 'Institution'}_${currentMedium}_Medium_Academic_Year_Comparison.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Matrix Table Columns
  const matrixColumns = [
    {
      title: 'Academic Year',
      dataIndex: 'academic_year',
      key: 'academic_year',
      width: 140,
      render: (text, r, idx) => (
        <div>
          <strong style={{ color: '#6366f1', fontSize: 13 }}>{text}</strong>
          {idx === comparisonData.length - 1 && (
            <Tag color="green" style={{ marginLeft: 6, fontSize: 10 }}>Latest</Tag>
          )}
        </div>
      )
    },
    {
      title: 'Candidates',
      dataIndex: 'total_candidates',
      key: 'total_candidates',
      width: 140,
      sorter: (a, b) => a.total_candidates - b.total_candidates,
      render: (count, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-color)' }}>{count}</div>
          {r.candidate_growth_pct !== null && (
            <div style={{ fontSize: 11, color: r.candidate_growth_pct >= 0 ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: 2 }}>
              {r.candidate_growth_pct >= 0 ? <RiseOutlined /> : <FallOutlined />}
              <span>{r.candidate_growth_pct > 0 ? `+${r.candidate_growth_pct}%` : `${r.candidate_growth_pct}%`}</span>
              <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>({r.candidate_growth_count > 0 ? `+${r.candidate_growth_count}` : r.candidate_growth_count})</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Medium Breakdown',
      key: 'medium_split',
      width: 200,
      render: (_, r) => {
        const mb = r.medium_breakdown || {};
        const keys = Object.keys(mb);
        if (keys.length === 0) return <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>General / Single</span>;
        return (
          <Space direction="vertical" size={2}>
            {keys.map(k => (
              <div key={k} style={{ fontSize: 11 }}>
                <Tag color={k === 'ENGLISH' ? 'blue' : k === 'KANNADA' ? 'orange' : 'purple'} style={{ fontSize: 10, margin: 0, padding: '0 4px' }}>
                  {k}
                </Tag>
                <span style={{ marginLeft: 5, fontWeight: 600 }}>{mb[k].total_candidates} candidates</span>
                <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: 4 }}>({mb[k].avg_percentage}% avg)</span>
              </div>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Average Score',
      dataIndex: 'avg_percentage',
      key: 'avg_percentage',
      width: 140,
      sorter: (a, b) => a.avg_percentage - b.avg_percentage,
      render: (pct, r) => {
        const val = Number(pct || 0);
        const badgeColor = val >= 75 ? 'green' : val >= 60 ? 'gold' : val >= 50 ? 'blue' : 'default';
        return (
          <div>
            <Tag color={badgeColor} style={{ fontWeight: 700, fontSize: 12 }}>
              {val}%
            </Tag>
            {r.avg_score_diff !== null && (
              <div style={{ fontSize: 11, color: r.avg_score_diff >= 0 ? '#10b981' : '#ef4444', marginTop: 2 }}>
                {r.avg_score_diff > 0 ? `+${r.avg_score_diff}%` : `${r.avg_score_diff}%`} YoY
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Pass Rate',
      dataIndex: 'pass_rate',
      key: 'pass_rate',
      width: 160,
      sorter: (a, b) => a.pass_rate - b.pass_rate,
      render: (rate, r) => {
        const num = Number(rate || 0);
        const color = num >= 90 ? '#10b981' : num >= 75 ? '#38bdf8' : num >= 60 ? '#f59e0b' : '#ef4444';
        return (
          <div style={{ minWidth: 100 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
              <span style={{ color }}>{num}%</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{r.passed_count}/{r.total_candidates}</span>
            </div>
            <Progress percent={num} size="small" strokeColor={color} showInfo={false} />
          </div>
        );
      }
    },
    {
      title: 'Result Breakdown',
      key: 'grades_breakdown',
      render: (_, r) => (
        <Space size={4} wrap>
          {r.distinction_count > 0 && <Tag color="purple" style={{ fontSize: 11 }}>⭐ Dist: {r.distinction_count}</Tag>}
          {r.first_class_count > 0 && <Tag color="green" style={{ fontSize: 11 }}>1st: {r.first_class_count}</Tag>}
          {r.second_class_count > 0 && <Tag color="gold" style={{ fontSize: 11 }}>2nd: {r.second_class_count}</Tag>}
          {r.pass_class_count > 0 && <Tag color="cyan" style={{ fontSize: 11 }}>Pass: {r.pass_class_count}</Tag>}
          {r.fail_count > 0 && <Tag color="red" style={{ fontSize: 11 }}>Fail: {r.fail_count}</Tag>}
        </Space>
      )
    },
    {
      title: 'Top Score',
      dataIndex: 'max_percentage',
      key: 'max_percentage',
      width: 100,
      render: (maxPct) => (
        <Tag color="magenta" style={{ fontWeight: 700 }}>
          🏆 {maxPct}%
        </Tag>
      )
    }
  ];

  // SVG Multi-Year Chart Dimensions & Calculation
  const svgWidth = 760;
  const svgHeight = 260;
  const padding = 45;

  const maxEnrollment = Math.max(...comparisonData.map(d => d.total_candidates), 10);
  const maxScoreVal = 100;

  const getChartX = (index) => {
    if (comparisonData.length <= 1) return svgWidth / 2;
    return padding + (index / (comparisonData.length - 1)) * (svgWidth - 2 * padding);
  };

  const getEnrollmentY = (val) => {
    return svgHeight - padding - (val / maxEnrollment) * (svgHeight - 2 * padding);
  };

  const getScoreY = (val) => {
    return svgHeight - padding - (val / maxScoreVal) * (svgHeight - 2 * padding);
  };

  const makeScorePath = (key) => {
    return comparisonData.reduce((acc, d, i) => {
      const x = getChartX(i);
      const y = getScoreY(d[key]);
      return i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`;
    }, '');
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24, flexWrap: 'wrap', gap: 10 }}>
          <Space size="middle" wrap>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-color)' }}>
              🏛️ {institution.institution_name || 'Institution'} - Academic Year Comparison
            </span>
            {institution.institution_medium && (
              <Tag color={institution.institution_medium === 'ENGLISH' ? 'blue' : institution.institution_medium === 'KANNADA' ? 'orange' : 'purple'}>
                {institution.institution_medium}
              </Tag>
            )}
          </Space>
          <Space wrap>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV}>
              Export CSV
            </Button>
            <Button size="small" icon={<SyncOutlined spin={loading} />} onClick={onRefresh}>
              Refresh
            </Button>
          </Space>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1280}
      style={{ top: 16, maxWidth: '96vw' }}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>
      ]}
      destroyOnClose
      styles={{ body: { maxHeight: '82vh', overflowY: 'auto', padding: '16px 12px' } }}
    >
      {/* Medium Scope Filter Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, background: 'rgba(255,255,255,0.03)', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color)', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Text strong style={{ color: 'var(--text-color)', fontSize: 13 }}>
            <ReadOutlined style={{ marginRight: 6, color: '#6366f1' }} />
            Candidate Medium Scope:
          </Text>
          <Segmented
            options={[
              { label: 'All Mediums', value: 'all' },
              ...(availableMediums.length > 0 
                ? availableMediums.map(m => ({ label: `${m} Medium`, value: m }))
                : [
                    { label: 'Kannada Medium', value: 'KANNADA' },
                    { label: 'English Medium', value: 'ENGLISH' }
                  ]
              )
            ]}
            value={currentMedium}
            onChange={handleMediumSelect}
          />
        </div>
        {currentMedium !== 'all' && (
          <Tag color="cyan" closable onClose={() => handleMediumSelect('all')} style={{ margin: 0 }}>
            Active Filter: {currentMedium} Medium
          </Tag>
        )}
      </div>

      {/* 4 Summary Statistic Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Total Students ({currentMedium === 'all' ? 'All Mediums' : currentMedium})</span>}
              value={summary.total_students_all_years || 0}
              valueStyle={{ color: '#6366f1', fontWeight: 'bold', fontSize: 22 }}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(168, 85, 247, 0.08)', border: '1px solid rgba(168, 85, 247, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Academic Years Recorded</span>}
              value={summary.total_academic_years || 0}
              valueStyle={{ color: '#a855f7', fontWeight: 'bold', fontSize: 22 }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Overall Average Score</span>}
              value={`${summary.overall_avg_score || 0}%`}
              valueStyle={{ color: '#10b981', fontWeight: 'bold', fontSize: 22 }}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: 10 }}>
            <Statistic
              title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>All-Time Highest Score</span>}
              value={`${summary.all_time_highest_score || 0}%`}
              valueStyle={{ color: '#f59e0b', fontWeight: 'bold', fontSize: 22 }}
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {comparisonData.length === 0 ? (
        <Empty description="No candidate data found for the selected medium across academic years." style={{ margin: '40px 0' }} />
      ) : (
        <Tabs
          defaultActiveKey="side_by_side_all_years"
          items={[
            {
              key: 'side_by_side_all_years',
              label: (
                <span>
                  <SwapOutlined style={{ marginRight: 6 }} />
                  Side-by-Side Dual/All Academic Year Deep-Dive (Default)
                </span>
              ),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* All Academic Years Side-by-Side Cards */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Text strong style={{ fontSize: 14, color: 'var(--text-color)' }}>
                        📊 All Academic Years Side-by-Side Comparison Cards
                      </Text>
                      <Tag color="purple">{comparisonData.length} Academic Years Recorded</Tag>
                    </div>

                    <Row gutter={[16, 16]} style={{ flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 8 }}>
                      {comparisonData.map((y, idx) => {
                        const isLatest = idx === comparisonData.length - 1;
                        const eng = y.medium_breakdown?.['ENGLISH'];
                        const kan = y.medium_breakdown?.['KANNADA'];
                        const topCand = y.top_candidates && y.top_candidates.length > 0 ? y.top_candidates[0] : null;

                        return (
                          <Col key={y.academic_year_id} style={{ minWidth: 280, maxWidth: 320, flex: '0 0 auto' }}>
                            <Card
                              size="small"
                              style={{
                                background: isLatest ? 'rgba(99, 102, 241, 0.06)' : 'var(--panel-color)',
                                border: isLatest ? '1.5px solid #6366f1' : '1px solid var(--border-color)',
                                borderRadius: 10,
                                height: '100%'
                              }}
                              title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontWeight: 700, fontSize: 14, color: isLatest ? '#6366f1' : 'var(--text-color)' }}>
                                    📅 {y.academic_year}
                                  </span>
                                  {isLatest && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Latest</Tag>}
                                </div>
                              }
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {/* Candidate Strength */}
                                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Total Candidates</Text>
                                    <strong style={{ fontSize: 16, color: 'var(--text-color)' }}>{y.total_candidates}</strong>
                                  </div>
                                  {y.candidate_growth_pct !== null && (
                                    <div style={{ fontSize: 11, color: y.candidate_growth_pct >= 0 ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                                      {y.candidate_growth_pct >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                      <span>{y.candidate_growth_pct > 0 ? `+${y.candidate_growth_pct}%` : `${y.candidate_growth_pct}%`} YoY ({y.candidate_growth_count > 0 ? `+${y.candidate_growth_count}` : y.candidate_growth_count})</span>
                                    </div>
                                  )}
                                </div>

                                {/* Medium Breakdown */}
                                <div>
                                  <Text style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Medium-Wise Breakdown</Text>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {eng && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, background: 'rgba(59, 130, 246, 0.08)', padding: '3px 6px', borderRadius: 4 }}>
                                        <Tag color="blue" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>ENGLISH</Tag>
                                        <span><strong>{eng.total_candidates}</strong> cands ({eng.avg_percentage}%)</span>
                                      </div>
                                    )}
                                    {kan && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, background: 'rgba(249, 115, 22, 0.08)', padding: '3px 6px', borderRadius: 4 }}>
                                        <Tag color="orange" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>KANNADA</Tag>
                                        <span><strong>{kan.total_candidates}</strong> cands ({kan.avg_percentage}%)</span>
                                      </div>
                                    )}
                                    {!eng && !kan && (
                                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>General / Single Medium</span>
                                    )}
                                  </div>
                                </div>

                                {/* Academic Performance */}
                                <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Avg Score</Text>
                                    <Tag color={y.avg_percentage >= 75 ? 'green' : y.avg_percentage >= 60 ? 'gold' : 'blue'} style={{ fontWeight: 700, margin: 0 }}>
                                      {y.avg_percentage}%
                                    </Tag>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                    <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Pass Rate</Text>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: y.pass_rate >= 75 ? '#10b981' : '#f59e0b' }}>
                                      {y.pass_rate}% ({y.passed_count}/{y.total_candidates})
                                    </span>
                                  </div>
                                  <Progress percent={y.pass_rate} size="small" strokeColor={y.pass_rate >= 75 ? '#10b981' : '#f59e0b'} showInfo={false} />
                                </div>

                                {/* Results Classification */}
                                <div style={{ fontSize: 11 }}>
                                  <Text style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Result Classifications</Text>
                                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    {y.distinction_count > 0 && <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>⭐ Dist: {y.distinction_count}</Tag>}
                                    {y.first_class_count > 0 && <Tag color="green" style={{ fontSize: 10, margin: 0 }}>1st: {y.first_class_count}</Tag>}
                                    {y.second_class_count > 0 && <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>2nd: {y.second_class_count}</Tag>}
                                    {y.pass_class_count > 0 && <Tag color="cyan" style={{ fontSize: 10, margin: 0 }}>Pass: {y.pass_class_count}</Tag>}
                                    {y.fail_count > 0 && <Tag color="red" style={{ fontSize: 10, margin: 0 }}>Fail: {y.fail_count}</Tag>}
                                  </div>
                                </div>

                                {/* Highest Score & Top Performer */}
                                <div style={{ borderTop: '1px dashed var(--border-color)', paddingTop: 8 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>Highest Score:</span>
                                    <strong style={{ color: '#ec4899' }}>🏆 {y.max_percentage}%</strong>
                                  </div>
                                  {topCand && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, marginTop: 4 }}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                                        🥇 {topCand.name}
                                      </span>
                                      {topCand.candidate_medium && (
                                        <Tag color={topCand.candidate_medium === 'ENGLISH' ? 'blue' : 'orange'} style={{ fontSize: 9, margin: 0, padding: '0 3px' }}>
                                          {topCand.candidate_medium}
                                        </Tag>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>

                  {/* Transposed Side-by-Side Comparison Matrix Table (All Academic Years as Columns) */}
                  <Card
                    size="small"
                    title={<span style={{ fontWeight: 700, color: 'var(--text-color)' }}>📋 All Academic Years Comparative Metrics Matrix</span>}
                    style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 10 }}
                  >
                    <Table
                      dataSource={[
                        {
                          key: 'candidates',
                          metric: 'Total Candidates Enrolled',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, y.total_candidates]))
                        },
                        {
                          key: 'growth',
                          metric: 'YoY Candidate Growth',
                          ...Object.fromEntries(comparisonData.map(y => [
                            y.academic_year, 
                            y.candidate_growth_pct !== null ? `${y.candidate_growth_pct > 0 ? '+' : ''}${y.candidate_growth_pct}%` : 'Base Year'
                          ]))
                        },
                        {
                          key: 'eng_candidates',
                          metric: 'English Medium Candidates',
                          ...Object.fromEntries(comparisonData.map(y => [
                            y.academic_year,
                            y.medium_breakdown?.['ENGLISH'] ? `${y.medium_breakdown['ENGLISH'].total_candidates} (${y.medium_breakdown['ENGLISH'].avg_percentage}%)` : '-'
                          ]))
                        },
                        {
                          key: 'kan_candidates',
                          metric: 'Kannada Medium Candidates',
                          ...Object.fromEntries(comparisonData.map(y => [
                            y.academic_year,
                            y.medium_breakdown?.['KANNADA'] ? `${y.medium_breakdown['KANNADA'].total_candidates} (${y.medium_breakdown['KANNADA'].avg_percentage}%)` : '-'
                          ]))
                        },
                        {
                          key: 'avg_score',
                          metric: 'Average Score (%)',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, `${y.avg_percentage}%`]))
                        },
                        {
                          key: 'pass_rate',
                          metric: 'Pass Rate (%)',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, `${y.pass_rate}%`]))
                        },
                        {
                          key: 'passed_count',
                          metric: 'Passed Candidates',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, `${y.passed_count} / ${y.total_candidates}`]))
                        },
                        {
                          key: 'distinction',
                          metric: 'Distinction Count (≥75%)',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, y.distinction_count]))
                        },
                        {
                          key: 'first_class',
                          metric: 'First Class Count (60-74%)',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, y.first_class_count]))
                        },
                        {
                          key: 'second_class',
                          metric: 'Second Class Count (50-59%)',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, y.second_class_count]))
                        },
                        {
                          key: 'failed',
                          metric: 'Failed Candidates (<35%)',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, y.fail_count]))
                        },
                        {
                          key: 'highest_score',
                          metric: 'Highest Score Recorded',
                          ...Object.fromEntries(comparisonData.map(y => [y.academic_year, `🏆 ${y.max_percentage}%`]))
                        },
                        {
                          key: 'topper',
                          metric: 'Batch Topper (Rank #1)',
                          ...Object.fromEntries(comparisonData.map(y => [
                            y.academic_year,
                            y.top_candidates && y.top_candidates.length > 0
                              ? `${y.top_candidates[0].name} (${y.top_candidates[0].percentage}%)`
                              : 'N/A'
                          ]))
                        }
                      ]}
                      columns={[
                        {
                          title: 'Metric / Performance Area',
                          dataIndex: 'metric',
                          key: 'metric',
                          width: 220,
                          fixed: 'left',
                          render: (text) => <strong style={{ color: '#6366f1' }}>{text}</strong>
                        },
                        ...comparisonData.map((y, idx) => ({
                          title: (
                            <div style={{ textAlign: 'center' }}>
                              <span style={{ fontWeight: 700, color: idx === comparisonData.length - 1 ? '#10b981' : 'var(--text-color)' }}>
                                {y.academic_year}
                              </span>
                              {idx === comparisonData.length - 1 && (
                                <Tag color="green" style={{ display: 'block', margin: '2px auto 0 auto', fontSize: 9, width: 'fit-content' }}>
                                  Latest
                                </Tag>
                              )}
                            </div>
                          ),
                          dataIndex: y.academic_year,
                          key: y.academic_year,
                          align: 'center',
                          render: (val, row) => {
                            if (val === undefined || val === null || val === '-') {
                              return <span style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>;
                            }
                            if (row.key === 'avg_score') {
                              return <Tag color="blue" style={{ fontWeight: 700 }}>{val}</Tag>;
                            }
                            if (row.key === 'pass_rate') {
                              return <Tag color="green" style={{ fontWeight: 700 }}>{val}</Tag>;
                            }
                            if (row.key === 'highest_score') {
                              return <Tag color="magenta" style={{ fontWeight: 700 }}>{val}</Tag>;
                            }
                            return <span style={{ fontWeight: 600 }}>{val}</span>;
                          }
                        }))
                      ]}
                      pagination={false}
                      size="small"
                      bordered
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>

                  {/* Dual Year Specific Comparator */}
                  <Card
                    size="small"
                    title={<span style={{ fontWeight: 700, color: 'var(--text-color)' }}>🔍 Specific 2-Year Direct Differentials Calculator</span>}
                    style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 10 }}
                  >
                    <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 16, background: 'rgba(255,255,255,0.02)', padding: 12, borderRadius: 8, border: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: '#6366f1', fontSize: 12 }}>
                          📌 Base Academic Year (Year 1)
                        </label>
                        <Select
                          value={year1Id}
                          onChange={setYear1Id}
                          style={{ width: '100%' }}
                        >
                          {comparisonData.map(y => (
                            <Option key={y.academic_year_id} value={y.academic_year_id}>
                              {y.academic_year} ({y.total_candidates} Students)
                            </Option>
                          ))}
                        </Select>
                      </div>

                      <div style={{ fontSize: 20, color: 'var(--text-secondary)' }}>
                        <SwapOutlined />
                      </div>

                      <div style={{ flex: 1, minWidth: 220 }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: '#10b981', fontSize: 12 }}>
                          🎯 Comparison Academic Year (Year 2)
                        </label>
                        <Select
                          value={year2Id}
                          onChange={setYear2Id}
                          style={{ width: '100%' }}
                        >
                          {comparisonData.map(y => (
                            <Option key={y.academic_year_id} value={y.academic_year_id}>
                              {y.academic_year} ({y.total_candidates} Students)
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </div>

                    {year1Data && year2Data ? (
                      <div>
                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                          <Col xs={24} sm={6}>
                            <Card size="small" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                              <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Candidates Difference</Text>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#6366f1' }}>{year1Data.total_candidates}</span>
                                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>vs</span>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#10b981' }}>{year2Data.total_candidates}</span>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: (year2Data.total_candidates - year1Data.total_candidates) >= 0 ? '#10b981' : '#ef4444' }}>
                                {(year2Data.total_candidates - year1Data.total_candidates) >= 0 ? '+' : ''}
                                {year2Data.total_candidates - year1Data.total_candidates} candidates (
                                {year1Data.total_candidates > 0 
                                  ? `${(((year2Data.total_candidates - year1Data.total_candidates) / year1Data.total_candidates) * 100).toFixed(1)}%` 
                                  : 'N/A'})
                              </div>
                            </Card>
                          </Col>

                          <Col xs={24} sm={6}>
                            <Card size="small" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                              <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Average Score Difference</Text>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#6366f1' }}>{year1Data.avg_percentage}%</span>
                                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>vs</span>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#10b981' }}>{year2Data.avg_percentage}%</span>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: (year2Data.avg_percentage - year1Data.avg_percentage) >= 0 ? '#10b981' : '#ef4444' }}>
                                {(year2Data.avg_percentage - year1Data.avg_percentage) >= 0 ? '+' : ''}
                                {(year2Data.avg_percentage - year1Data.avg_percentage).toFixed(2)}% difference
                              </div>
                            </Card>
                          </Col>

                          <Col xs={24} sm={6}>
                            <Card size="small" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                              <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Pass Rate Difference</Text>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#6366f1' }}>{year1Data.pass_rate}%</span>
                                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>vs</span>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#10b981' }}>{year2Data.pass_rate}%</span>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: (year2Data.pass_rate - year1Data.pass_rate) >= 0 ? '#10b981' : '#ef4444' }}>
                                {(year2Data.pass_rate - year1Data.pass_rate) >= 0 ? '+' : ''}
                                {(year2Data.pass_rate - year1Data.pass_rate).toFixed(2)}% difference
                              </div>
                            </Card>
                          </Col>

                          <Col xs={24} sm={6}>
                            <Card size="small" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                              <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Distinctions Count</Text>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#6366f1' }}>{year1Data.distinction_count}</span>
                                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>vs</span>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#10b981' }}>{year2Data.distinction_count}</span>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: (year2Data.distinction_count - year1Data.distinction_count) >= 0 ? '#10b981' : '#ef4444' }}>
                                {(year2Data.distinction_count - year1Data.distinction_count) >= 0 ? '+' : ''}
                                {year2Data.distinction_count - year1Data.distinction_count} distinctions
                              </div>
                            </Card>
                          </Col>
                        </Row>

                        <Row gutter={[12, 12]}>
                          <Col xs={24} md={12}>
                            <Card 
                              size="small" 
                              title={<span style={{ color: '#6366f1', fontWeight: 700 }}>📌 {year1Data.academic_year} Summary</span>}
                              style={{ background: 'var(--panel-color)', border: '1px solid #6366f1', borderRadius: 8 }}
                            >
                              <Space direction="vertical" style={{ width: '100%' }} size="small">
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span>Passed vs Failed:</span>
                                  <strong>✅ {year1Data.passed_count} Passed / ❌ {year1Data.fail_count} Failed</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span>Highest Score:</span>
                                  <strong style={{ color: '#ec4899' }}>🏆 {year1Data.max_percentage}%</strong>
                                </div>
                                <Divider style={{ margin: '6px 0', fontSize: 11 }}>Top Performers</Divider>
                                {year1Data.top_candidates && year1Data.top_candidates.length > 0 ? (
                                  year1Data.top_candidates.map((c, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, alignItems: 'center' }}>
                                      <span>#{idx + 1} {c.name} {c.candidate_medium && <Tag color={c.candidate_medium === 'ENGLISH' ? 'blue' : 'orange'} style={{ fontSize: 9, margin: 0, padding: '0 3px' }}>{c.candidate_medium}</Tag>}</span>
                                      <Tag color="purple">{c.percentage}% ({c.result || 'PASS'})</Tag>
                                    </div>
                                  ))
                                ) : (
                                  <Text style={{ color: 'var(--text-secondary)', fontSize: 11 }}>No individual candidate records</Text>
                                )}
                              </Space>
                            </Card>
                          </Col>

                          <Col xs={24} md={12}>
                            <Card 
                              size="small" 
                              title={<span style={{ color: '#10b981', fontWeight: 700 }}>🎯 {year2Data.academic_year} Summary</span>}
                              style={{ background: 'var(--panel-color)', border: '1px solid #10b981', borderRadius: 8 }}
                            >
                              <Space direction="vertical" style={{ width: '100%' }} size="small">
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span>Passed vs Failed:</span>
                                  <strong>✅ {year2Data.passed_count} Passed / ❌ {year2Data.fail_count} Failed</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span>Highest Score:</span>
                                  <strong style={{ color: '#ec4899' }}>🏆 {year2Data.max_percentage}%</strong>
                                </div>
                                <Divider style={{ margin: '6px 0', fontSize: 11 }}>Top Performers</Divider>
                                {year2Data.top_candidates && year2Data.top_candidates.length > 0 ? (
                                  year2Data.top_candidates.map((c, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, alignItems: 'center' }}>
                                      <span>#{idx + 1} {c.name} {c.candidate_medium && <Tag color={c.candidate_medium === 'ENGLISH' ? 'blue' : 'orange'} style={{ fontSize: 9, margin: 0, padding: '0 3px' }}>{c.candidate_medium}</Tag>}</span>
                                      <Tag color="green">{c.percentage}% ({c.result || 'PASS'})</Tag>
                                    </div>
                                  ))
                                ) : (
                                  <Text style={{ color: 'var(--text-secondary)', fontSize: 11 }}>No individual candidate records</Text>
                                )}
                              </Space>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    ) : null}
                  </Card>
                </Space>
              )
            },
            {
              key: 'trends',
              label: (
                <span>
                  <BarChartOutlined style={{ marginRight: 6 }} />
                  Visual Trends & Growth
                </span>
              ),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* Candidate Enrollment Trend */}
                  <Card 
                    size="small" 
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ color: 'var(--text-color)' }}>
                          👥 Candidate Enrollment by Academic Year {currentMedium !== 'all' ? `(${currentMedium} Medium)` : ''}
                        </Text>
                      </div>
                    }
                    style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 10 }}
                  >
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: 'auto', minWidth: 620 }}>
                        {/* Grid Lines */}
                        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                          const y = padding + pct * (svgHeight - 2 * padding);
                          return (
                            <g key={i}>
                              <line x1={padding} y1={y} x2={svgWidth - padding} y2={y} stroke="rgba(150, 150, 150, 0.15)" strokeDasharray="3 3" />
                              <text x={padding - 6} y={y + 4} textAnchor="end" fontSize={10} fill="var(--text-secondary, #9ca3af)">
                                {Math.round(maxEnrollment - (pct * maxEnrollment))}
                              </text>
                            </g>
                          );
                        })}

                        {/* Bars per Year */}
                        {comparisonData.map((d, i) => {
                          const x = getChartX(i);
                          const barWidth = Math.max(20, Math.min(42, (svgWidth - 2 * padding) / (comparisonData.length * 2)));
                          const totalHeight = (d.total_candidates / maxEnrollment) * (svgHeight - 2 * padding);
                          const baseY = svgHeight - padding;
                          const barY = baseY - totalHeight;

                          return (
                            <g key={i}>
                              <rect
                                x={x - barWidth / 2}
                                y={barY}
                                width={barWidth}
                                height={totalHeight}
                                fill="url(#barGradient)"
                                rx={4}
                              />
                              <text
                                x={x}
                                y={barY - 6}
                                textAnchor="middle"
                                fontSize={11}
                                fontWeight="bold"
                                fill="var(--text-color, #ffffff)"
                              >
                                {d.total_candidates}
                              </text>
                              <text
                                x={x}
                                y={svgHeight - 12}
                                textAnchor="middle"
                                fontSize={10}
                                fill="var(--text-secondary, #9ca3af)"
                                transform={comparisonData.length > 7 ? `rotate(-30 ${x} ${svgHeight - 12})` : ''}
                              >
                                {d.academic_year}
                              </text>
                            </g>
                          );
                        })}

                        <defs>
                          <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6366f1" />
                            <stop offset="100%" stopColor="#3b82f6" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                  </Card>

                  {/* Academic Score & Pass Rate Trend */}
                  <Card 
                    size="small" 
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ color: 'var(--text-color)' }}>📈 Average Score % & Pass Rate % Trend</Text>
                        <Space size="middle">
                          <Space size={4}>
                            <span style={{ width: 12, height: 3, background: '#10b981', display: 'inline-block' }} />
                            <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Avg Score %</Text>
                          </Space>
                          <Space size={4}>
                            <span style={{ width: 12, height: 3, background: '#f59e0b', display: 'inline-block' }} />
                            <Text style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Pass Rate %</Text>
                          </Space>
                        </Space>
                      </div>
                    }
                    style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 10 }}
                  >
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: 'auto', minWidth: 620 }}>
                        {[0, 25, 50, 75, 100].map((val, i) => {
                          const y = getScoreY(val);
                          return (
                            <g key={i}>
                              <line x1={padding} y1={y} x2={svgWidth - padding} y2={y} stroke="rgba(150, 150, 150, 0.15)" strokeDasharray="3 3" />
                              <text x={padding - 6} y={y + 4} textAnchor="end" fontSize={10} fill="var(--text-secondary, #9ca3af)">
                                {val}%
                              </text>
                            </g>
                          );
                        })}

                        <path d={makeScorePath('avg_percentage')} fill="none" stroke="#10b981" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
                        <path d={makeScorePath('pass_rate')} fill="none" stroke="#f59e0b" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />

                        {comparisonData.map((d, i) => {
                          const x = getChartX(i);
                          const yAvg = getScoreY(d.avg_percentage);
                          const yPass = getScoreY(d.pass_rate);

                          return (
                            <g key={i}>
                              <circle cx={x} cy={yAvg} r={4.5} fill="#10b981" stroke="#fff" strokeWidth={1.5} />
                              <circle cx={x} cy={yPass} r={4.5} fill="#f59e0b" stroke="#fff" strokeWidth={1.5} />

                              <text x={x} y={yAvg - 8} textAnchor="middle" fontSize={9} fill="#10b981" fontWeight="600">
                                {d.avg_percentage}%
                              </text>
                              <text x={x} y={yPass + 14} textAnchor="middle" fontSize={9} fill="#f59e0b" fontWeight="600">
                                {d.pass_rate}%
                              </text>

                              <text
                                x={x}
                                y={svgHeight - 12}
                                textAnchor="middle"
                                fontSize={10}
                                fill="var(--text-secondary, #9ca3af)"
                                transform={comparisonData.length > 7 ? `rotate(-30 ${x} ${svgHeight - 12})` : ''}
                              >
                                {d.academic_year}
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  </Card>
                </Space>
              )
            },
            {
              key: 'matrix',
              label: (
                <span>
                  <CalendarOutlined style={{ marginRight: 6 }} />
                  Year-by-Year Comparison Table
                </span>
              ),
              children: (
                <Table
                  dataSource={comparisonData}
                  columns={matrixColumns}
                  rowKey="academic_year_id"
                  size="small"
                  pagination={false}
                  bordered
                />
              )
            },
            {
              key: 'medium_wise_comparison',
              label: (
                <span>
                  <ReadOutlined style={{ marginRight: 6 }} />
                  Medium-Wise Breakdown Table
                </span>
              ),
              children: (
                <div>
                  <Table
                    dataSource={comparisonData}
                    rowKey="academic_year_id"
                    size="small"
                    pagination={false}
                    bordered
                    columns={[
                      {
                        title: 'Academic Year',
                        dataIndex: 'academic_year',
                        key: 'academic_year',
                        width: 140,
                        render: t => <strong style={{ color: '#6366f1' }}>{t}</strong>
                      },
                      {
                        title: 'English Medium Candidates',
                        key: 'eng_cand',
                        render: (_, r) => {
                          const eng = r.medium_breakdown?.['ENGLISH'];
                          if (!eng) return <span style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>;
                          return (
                            <div>
                              <div><strong>{eng.total_candidates}</strong> candidates</div>
                              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                                Avg: <Tag color="blue">{eng.avg_percentage}%</Tag> Pass Rate: <strong style={{ color: '#10b981' }}>{eng.pass_rate}%</strong>
                              </div>
                            </div>
                          );
                        }
                      },
                      {
                        title: 'Kannada Medium Candidates',
                        key: 'kan_cand',
                        render: (_, r) => {
                          const kan = r.medium_breakdown?.['KANNADA'];
                          if (!kan) return <span style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>;
                          return (
                            <div>
                              <div><strong>{kan.total_candidates}</strong> candidates</div>
                              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                                Avg: <Tag color="orange">{kan.avg_percentage}%</Tag> Pass Rate: <strong style={{ color: '#10b981' }}>{kan.pass_rate}%</strong>
                              </div>
                            </div>
                          );
                        }
                      },
                      {
                        title: 'Total Candidates (All Mediums)',
                        key: 'total_comb',
                        width: 170,
                        render: (_, r) => (
                          <div>
                            <strong style={{ fontSize: 14 }}>{r.total_candidates}</strong>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                              Avg: <Tag color="green">{r.avg_percentage}%</Tag> Pass: <strong>{r.pass_rate}%</strong>
                            </div>
                          </div>
                        )
                      }
                    ]}
                  />
                </div>
              )
            },
            {
              key: 'top_performers',
              label: (
                <span>
                  <TrophyOutlined style={{ marginRight: 6 }} />
                  Top Performers by Year
                </span>
              ),
              children: (
                <Row gutter={[16, 16]}>
                  {comparisonData.map((y) => (
                    <Col xs={24} sm={12} md={8} key={y.academic_year_id}>
                      <Card 
                        size="small" 
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, color: '#6366f1' }}>{y.academic_year}</span>
                            <Tag color="cyan">{y.total_candidates} Candidates</Tag>
                          </div>
                        }
                        style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 8 }}
                      >
                        {y.top_candidates && y.top_candidates.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {y.top_candidates.map((cand, idx) => (
                              <div 
                                key={cand.candidate_id || idx}
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '4px 6px',
                                  background: idx === 0 ? 'rgba(245, 158, 11, 0.08)' : 'rgba(255,255,255,0.02)',
                                  borderRadius: 6,
                                  borderLeft: idx === 0 ? '3px solid #f59e0b' : '1px solid transparent'
                                }}
                              >
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <strong style={{ fontSize: 12, color: 'var(--text-color)' }}>
                                      {idx === 0 ? '🥇 ' : idx === 1 ? '🥈 ' : idx === 2 ? '🥉 ' : `#${idx + 1} `}
                                      {cand.name}
                                    </strong>
                                    {cand.candidate_medium && (
                                      <Tag color={cand.candidate_medium === 'ENGLISH' ? 'blue' : 'orange'} style={{ fontSize: 9, margin: 0, padding: '0 3px' }}>
                                        {cand.candidate_medium}
                                      </Tag>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                                    {cand.combination || 'General'}
                                  </div>
                                </div>
                                <div style={{ orientation: 'right', textAlign: 'right' }}>
                                  <Tag color={cand.percentage >= 85 ? 'purple' : cand.percentage >= 60 ? 'green' : 'blue'} style={{ fontWeight: 700, margin: 0 }}>
                                    {cand.percentage}%
                                  </Tag>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Text style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No specific topper records found.</Text>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              )
            }
          ]}
        />
      )}
    </Modal>
  );
};

export default InstitutionAcademicYearComparisonModal;
